import { useState } from 'react';
import { Send, Plus, Search, AlertCircle, MessageSquare, Megaphone, FileBadge } from 'lucide-react';
import { useWhatsAppCampaigns, useSendBroadcastCampaign, useSelectableUsers, useCreateBroadcastCampaign, useApprovedTemplates, useSendTemplateBroadcast } from '../../hooks/useWhatsApp.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import TemplatesTab from './components/TemplatesTab.jsx';
import TemplatePicker, { templatePickerToPayload } from './components/TemplatePicker.jsx';
import { getApiErrorMessage } from '../../lib/api.js';

function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState('broadcasts'); // 'broadcasts' | 'templates'
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [campaignName, setCampaignName] = useState('');
  const [campaignMessage, setCampaignMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Template flow inside the Create-Campaign form
  const [messageType, setMessageType] = useState('template'); // 'template' | 'text'
  const [templateValue, setTemplateValue] = useState({ selectedKey: null, params: [] });
  const { data: approvedTemplatesResp } = useApprovedTemplates();
  const approvedTemplates = approvedTemplatesResp?.data?.data || [];
  const sendTemplateMutation = useSendTemplateBroadcast();
  
  // Fetch campaigns
  const { data: campaignsData, isLoading: campaignsLoading } = useWhatsAppCampaigns({ limit: 100 });
  const { data: usersData, isLoading: usersLoading, error: usersError } = useSelectableUsers({ 
    page: 1, 
    limit: 10000  // Fetch all users at once
  });
  const createCampaignMutation = useCreateBroadcastCampaign();
  const sendBroadcastMutation = useSendBroadcastCampaign();

  const campaigns = campaignsData?.data?.data || [];
  
  // Extract users array from axios response structure
  // Response structure: axios wraps it in .data, then our API wraps it again
  // So: usersData.data.data contains the actual users array
  let allUsers = [];
  
  if (usersData?.data?.data && Array.isArray(usersData.data.data)) {
    allUsers = usersData.data.data;
  }
  
  const filteredUsers = allUsers.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone_number?.includes(searchTerm)
  );

  const handleToggleUser = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...filteredUsers]);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      setError('Please enter a campaign name');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    // ---- Template path: send approved Meta template directly (one-step) ----
    if (messageType === 'template') {
      const payload = templatePickerToPayload(templateValue, approvedTemplates);
      if (!payload) {
        setError('Please select an approved template');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        await sendTemplateMutation.mutateAsync({
          ...payload,
          campaign_name: campaignName,
          target: { type: 'user_ids', user_ids: selectedUsers.map((u) => u.id) },
        });
        // Reset form
        setShowUserSelection(false);
        setSelectedUsers([]);
        setCampaignName('');
        setCampaignMessage('');
        setTemplateValue({ selectedKey: null, params: [] });
        setSearchTerm('');
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to send template'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // ---- Free-text path: existing two-step (create draft, then send) ----
    if (!campaignMessage.trim()) {
      setError('Please enter a message');
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const campaignData = {
        name: campaignName,
        message: campaignMessage,
        target_users: selectedUsers.map(u => u.id)
      };

      await createCampaignMutation.mutateAsync(campaignData);

      // Reset form
      setShowUserSelection(false);
      setSelectedUsers([]);
      setCampaignName('');
      setCampaignMessage('');
      setSearchTerm('');
    } catch (err) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this broadcast campaign?')) {
      return;
    }
    try {
      await sendBroadcastMutation.mutateAsync(campaignId);
      alert('Broadcast campaign sent successfully!');
    } catch (error) {
      alert('Failed to send campaign: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { variant: 'secondary', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
      sending: { variant: 'warning', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
      completed: { variant: 'success', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
      failed: { variant: 'danger', color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' }
    };
    return variants[status] || variants.draft;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              Campaigns
            </h1>
            {activeTab === 'broadcasts' && (
              <Badge variant="secondary" className="text-xs">
                {campaigns.length} campaigns
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'broadcasts' && !showUserSelection && (
              <Button variant="primary" size="sm" onClick={() => setShowUserSelection(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Campaign
              </Button>
            )}
            {activeTab === 'broadcasts' && showUserSelection && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => {
                  setShowUserSelection(false);
                  setSelectedUsers([]);
                  setCampaignName('');
                  setCampaignMessage('');
                }}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleCreateCampaign} disabled={loading}>
                  {loading
                    ? (messageType === 'template' ? 'Sending...' : 'Creating...')
                    : (messageType === 'template' ? 'Send Template' : 'Create Campaign')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setActiveTab('broadcasts')}
            className={
              'px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ' +
              (activeTab === 'broadcasts'
                ? 'border-brand-primary text-brand-primary dark:text-white dark:border-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')
            }
          >
            <Megaphone className="h-4 w-4" />
            Broadcasts
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('templates')}
            className={
              'px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ' +
              (activeTab === 'templates'
                ? 'border-brand-primary text-brand-primary dark:text-white dark:border-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')
            }
          >
            <FileBadge className="h-4 w-4" />
            Templates
          </button>
        </div>

        {activeTab === 'templates' && <TemplatesTab />}
        {activeTab === 'broadcasts' && (
        <>
        {/* --- begin original broadcasts UI (closes near end of component) --- */}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* User Selection View */}
        {showUserSelection && (
          <div className="space-y-6 mb-6">
            {/* Campaign Form */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Campaign Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="e.g., Monthly Newsletter"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-700"
                  />
                </div>

                {/* Message type selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message type *
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="msg-type"
                        checked={messageType === 'template'}
                        onChange={() => setMessageType('template')}
                      />
                      <FileBadge className="h-4 w-4" /> Approved Meta template
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="msg-type"
                        checked={messageType === 'text'}
                        onChange={() => setMessageType('text')}
                      />
                      <MessageSquare className="h-4 w-4" /> Free text
                      <span className="text-xs text-gray-500 dark:text-gray-400">(24h customer window only)</span>
                    </label>
                  </div>
                </div>

                {messageType === 'template' ? (
                  <TemplatePicker
                    value={templateValue}
                    onChange={setTemplateValue}
                    sampleRecipient={selectedUsers[0] || filteredUsers[0] || allUsers[0] || null}
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={campaignMessage}
                      onChange={(e) => setCampaignMessage(e.target.value)}
                      placeholder="Enter your broadcast message..."
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-700 resize-none"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {campaignMessage.length} characters
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* User Selection */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Recipients</h2>
                <div className="flex items-center gap-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedUsers.length} of {filteredUsers.length} selected
                  </p>
                  <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                    {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-700"
                />
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Designation</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Phone</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersError ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-red-500 dark:text-red-400">
                          <AlertCircle className="mx-auto h-12 w-12 mb-3" />
                          Error loading users: {usersError.message}
                        </td>
                      </tr>
                    ) : usersLoading ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-gray-500 dark:text-gray-400">
                          Loading users...
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-gray-500 dark:text-gray-400">
                          No users found (Total: {allUsers.length})
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const isSelected = selectedUsers.some(u => u.id === user.id);
                        return (
                          <tr
                            key={user.id}
                            className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                              isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                            }`}
                            onClick={() => handleToggleUser(user)}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleUser(user)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                              {user.full_name || `${user.first_name} ${user.last_name}`}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                              {user.designation_name || 'No designation'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                              {user.phone_number || 'No phone'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                              {user.company_name || 'No company'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Campaigns Table */}
        {!showUserSelection && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Campaigns</h2>
            
            {campaignsLoading ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                Loading campaigns...
              </div>
            ) : campaigns.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                <MessageSquare className="mx-auto h-12 w-12 mb-3 text-gray-400" />
                <p>No campaigns found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Message</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Recipients</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Sent</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Failed</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => {
                      const statusInfo = getStatusBadge(campaign.status);
                      return (
                        <tr key={campaign.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900 dark:text-white">{campaign.name}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                              {campaign.message}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={statusInfo.variant}
                              className={statusInfo.color}
                              size="sm"
                            >
                              {campaign.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {campaign.total_recipients || 0}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {campaign.sent_count || 0}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {campaign.failed_count || 0}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendCampaign(campaign.id)}
                              disabled={campaign.status === 'sending' || campaign.status === 'completed'}
                            >
                              <Send className="mr-1 h-4 w-4" />
                              {campaign.status === 'completed' ? 'Sent' : campaign.status === 'sending' ? 'Sending...' : 'Send'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
        {/* --- end original broadcasts UI --- */}
        </>
        )}
      </div>
    </div>
  );
}

export default WhatsAppPage;
